import {
  FAQ_ASK_PATH,
  FAQ_ASK_REFUSALS,
  type FaqAskEvent,
  type FaqAskRefusal,
} from "@shared/faq/ask-protocol";
import { isRecord } from "@shared/validation/is-record";
import { getBrowserSessionId } from "@/lib/browser-session-id";
import { convexSiteUrl } from "@/lib/convex/site-url";
import { publicConfigValue } from "@/lib/public-config";

const SESSION_STORAGE_KEY = "repackify.faq-ask-session";

export type FaqAskOutcome =
  | { kind: "refused"; refusal: FaqAskRefusal }
  | { kind: "failed" }
  | { kind: "answered"; done: Extract<FaqAskEvent, { type: "done" }> };

function parseEvent(line: string): FaqAskEvent | null {
  try {
    const parsed: unknown = JSON.parse(line);
    if (!isRecord(parsed)) return null;
    if (
      (parsed.type === "delta" || parsed.type === "replace") &&
      typeof parsed.text === "string"
    ) {
      return { type: parsed.type, text: parsed.text };
    }
    if (parsed.type === "error") return { type: "error" };
    if (
      parsed.type === "done" &&
      typeof parsed.questionId === "string" &&
      typeof parsed.isGrounded === "boolean" &&
      Array.isArray(parsed.sources)
    ) {
      return {
        type: "done",
        questionId: parsed.questionId,
        isGrounded: parsed.isGrounded,
        sources: parsed.sources.flatMap((source: unknown) =>
          isRecord(source) &&
          typeof source.title === "string" &&
          typeof source.path === "string"
            ? [{ title: source.title, path: source.path }]
            : [],
        ),
      };
    }
    return null;
  } catch {
    return null;
  }
}

async function readRefusal(response: Response): Promise<FaqAskOutcome> {
  const body: unknown = await response.json().catch(() => null);
  const supplied = isRecord(body) ? body.refusal : undefined;
  const refusal = FAQ_ASK_REFUSALS.find((candidate) => candidate === supplied);
  return refusal ? { kind: "refused", refusal } : { kind: "failed" };
}

export async function askFaq(args: {
  question: string;
  pagePath: string;
  signal: AbortSignal;
  onDelta: (text: string) => void;
  onReplace: (text: string) => void;
}): Promise<FaqAskOutcome> {
  const baseUrl = convexSiteUrl(
    publicConfigValue("NEXT_PUBLIC_CONVEX_SITE_URL"),
    publicConfigValue("NEXT_PUBLIC_CONVEX_URL"),
  );
  const response = await fetch(`${baseUrl}${FAQ_ASK_PATH}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      question: args.question,
      pagePath: args.pagePath,
      sessionId: getBrowserSessionId(SESSION_STORAGE_KEY),
    }),
    signal: args.signal,
  });
  if (!response.ok || !response.body) return readRefusal(response);

  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffered = "";
  for (;;) {
    const { done, value } = await reader.read();
    buffered += value ?? "";
    const lines = buffered.split("\n");
    buffered = lines.pop() ?? "";
    for (const line of lines) {
      const event = line ? parseEvent(line) : null;
      if (event?.type === "delta") args.onDelta(event.text);
      if (event?.type === "replace") args.onReplace(event.text);
      if (event?.type === "done") return { kind: "answered", done: event };
      if (event?.type === "error") return { kind: "failed" };
    }
    if (done) return { kind: "failed" };
  }
}
